import React, { useEffect, useState } from "react";
import { fetchData } from "../../../utils/functions";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import {
  AddButton,
  CustomConfirmModal,
  PaginationDropdown,
  ThinDropdownWithSearch,
} from "../../../components/common/GeneralComponent";
import { Accordion, AccordionDetails, AccordionSummary } from "@mui/material";
import DataTable from "react-data-table-component";
import { Minus, Plus, Download } from "lucide-react";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Loader from "../../../components/partials/Loader";
import * as XLSX from "xlsx";

import GeneralFooter from "../../common/GeneralFooter";
import PermissionButton from "../../common/PermissionButton";

const Deh = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user: loggedInUser } = useSelector((state) => state.auth);
  const permissions = loggedInUser?.user_permissions || [];

  const [dehs, setDehs] = useState([]);
  const [search, setSearch] = useState("");
  const [isModalVisible, setModalVisible] = useState(false);
  const [selectedDehForDelete, setSelectedDehForDelete] = useState(null);
  const [isFiltersVisible, setIsFiltersVisible] = useState(false);

  const [districts, setDistricts] = useState([]);
  const [tehsils, setTehsils] = useState([]);
  const [ucs, setUcs] = useState([]);

  const [selectedDistrict, setSelectedDistrict] = useState(null);
  const [selectedTehsil, setSelectedTehsil] = useState(null);
  const [selectedUc, setSelectedUc] = useState(null);

  const [tempDistrict, setTempDistrict] = useState(null);
  const [tempTehsil, setTempTehsil] = useState(null);
  const [tempUc, setTempUc] = useState(null);

  const [isDeleting, setIsDeleting] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 10,
    totalItems: 0,
    totalPages: 0,
  });
  const [loader, setLoader] = useState(true);
  const fetchDehs = async () => {
    try {
      const response = await fetchData(
        "all-dehs",
        "GET",
        null,
        {},
        {
          search: search || undefined,
          page: pagination.currentPage,
          limit: pagination.pageSize,
          district_id: selectedDistrict || undefined,
          tehsil_id: selectedTehsil || undefined,
          uc_id: selectedUc || undefined,
        },
        navigate,
        "/dehs",
        dispatch
      );
      setDehs(response?.data || []);
      setPagination(response?.pagination || {});
    } catch (error) {
      console.error("Error fetching dehs:", error);
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    fetchDehs();
  }, [
    pagination.currentPage,
    pagination.pageSize,
    search,
    selectedDistrict,
    selectedTehsil,
    selectedUc,
  ]);
  const fetchDistricts = async () => {
    try {
      const res = await fetchData(
        "districts",
        "GET",
        null,
        {},
        {},
        navigate,
        "/districts",
        dispatch
      );
      setDistricts(res?.data || []);
    } catch (error) {
      console.error("Error fetching districts:", error);
    }
  };
  const fetchTehsils = async () => {
    if (!tempDistrict) {
      setTehsils([]);
      return;
    }
    try {
      const res = await fetchData(
        "tehsils",
        "GET",
        null,
        {},
        { district_id: tempDistrict },
        navigate,
        "/tehsils",
        dispatch
      );
      setTehsils(res?.data || []);
    } catch (error) {
      console.error("Error fetching tehsils:", error);
    }
  };
  const fetchUcs = async () => {
    if (!tempTehsil) {
      setUcs([]);
      return;
    }
    try {
      const res = await fetchData(
        "ucs",
        "GET",
        null,
        {},
        { tehsil_id: tempTehsil },
        navigate,
        "/ucs",
        dispatch
      );
      setUcs(res?.data || []);
    } catch (error) {
      console.error("Error fetching UCs:", error);
    }
  };
  const handleDistrictChange = (val) => {
    setTempDistrict(val);
    setTempTehsil(null);
    setTempUc(null);
    setTehsils([]);
    setUcs([]);
  };

  const handleTehsilChange = (val) => {
    setTempTehsil(val);
    setTempUc(null);
    setUcs([]);
  };

  const handleUcChange = (val) => {
    setTempUc(val);
  };

  const clearFilters = () => {
    setSearch("");
    setTempDistrict(null);
    setTempTehsil(null);
    setTempUc(null);

    setSelectedDistrict(null);
    setSelectedTehsil(null);
    setSelectedUc(null);

    setDistricts([]);
    setTehsils([]);
    setUcs([]);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));

    fetchDistricts();
  };

  const applyFilters = () => {
    setSelectedDistrict(tempDistrict);
    setSelectedTehsil(tempTehsil);
    setSelectedUc(tempUc);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  useEffect(() => {
    fetchDistricts();
  }, []);

  useEffect(() => {
    fetchTehsils();
  }, [tempDistrict]);

  useEffect(() => {
    fetchUcs();
  }, [tempTehsil]);
  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };
  const handlePageSizeChange = (size) => {
    setPagination((prev) => ({ ...prev, pageSize: size, currentPage: 1 }));
  };
  const handlePageChange = (event, value) => {
    setPagination((prev) => ({ ...prev, currentPage: value }));
  };
  const handleDeleteConfirm = async () => {
    if (!selectedDehForDelete) return;
    setIsDeleting(true);
    try {
      await fetchData(
        "dehs",
        "DELETE",
        { deh_id: selectedDehForDelete.id },
        {},
        {},
        navigate,
        "/dehs",
        dispatch
      );
      toast.success("Deh deleted successfully.");
      setModalVisible(false);
      setSelectedDehForDelete(null);
      await fetchDehs();
    } catch (error) {
      console.error("Error deleting Deh:", error);
      toast.error(error?.message || "Failed to delete Deh.");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setModalVisible(false);
    setSelectedDehForDelete(null);
  };

  const showDeleteModal = (row) => {
    const dehId = row.deh_id || row.Deh_Id;
    const dehName = row.deh_name || row.Deh_Name;
    setSelectedDehForDelete({ id: dehId, name: dehName });
    setModalVisible(true);
  };

  const toggleFilters = () => setIsFiltersVisible((prev) => !prev);
  const exportToExcel = async () => {
    setIsExporting(true);
    try {
      const response = await fetchData(
        "all-dehs",
        "GET",
        null,
        {},
        {
          search: search || undefined,
          district_id: selectedDistrict || undefined,
          tehsil_id: selectedTehsil || undefined,
          uc_id: selectedUc || undefined,
          limit: 999999,
          page: 1,
        },
        navigate,
        "/dehs",
        dispatch
      );

      const exportData = response?.data || [];
      if (exportData.length === 0) {
        toast.warning("No data to export");
        return;
      }

      const excelData = exportData.map((deh, index) => ({
        "Sr.No": index + 1,
        "Deh Name": deh.deh_name || deh.Deh_Name || "-",
        //"UC Name": deh.UC?.uc_name || deh.uc?.uc_name || "N/A",
        "Tehsil Name":
          deh.Tehsil?.tehsil_name || deh.tehsil?.tehsil_name || "N/A",
        "District Name":
          deh.District?.district_name || deh.district?.district_name || "N/A",
        Status: deh.status === 1 ? "Active" : "In-Active",
      }));

      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.json_to_sheet(excelData);
      worksheet["!cols"] = [
        { wch: 8 },
        { wch: 20 },
        { wch: 20 },
        { wch: 20 },
        { wch: 20 },
        { wch: 12 },
      ];
      XLSX.utils.book_append_sheet(workbook, worksheet, "Dehs");
      const currentDate = new Date().toISOString().split("T")[0];
      XLSX.writeFile(workbook, `Dehs_Export_${currentDate}.xlsx`);
      toast.success(`Excel file exported successfully`);
    } catch (error) {
      console.error("Error exporting Excel:", error);
      toast.error("Failed to export Excel file");
    } finally {
      setIsExporting(false);
    }
  };
  const customStyles = {
    headCells: {
      style: {
        border: "1px solid #f3f3f3",
        color: "black",
        fontWeight: "bold",
        fontSize: "1.075rem",
        textAlign: "center",
      },
    },
    cells: { style: { border: "1px solid #f3f3f3", textAlign: "center" } },
  };
  const columns = [
    {
      name: <span className="text-[0.813rem]">Sr.No</span>,
      cell: (row, index) => (
        <span>
          {(pagination.currentPage - 1) * pagination.pageSize + index + 1}
        </span>
      ),
      width: "80px",
    },
    {
      name: <span className="text-[0.813rem]">Deh Name</span>,
      selector: (row) => row.deh_name || row.Deh_Name || "-",
      sortable: true,
      width: "250px",
    },
    // {
    //   name: <span className="text-[0.813rem]">UC Name</span>,
    //   selector: (row) => row.UC?.uc_name || row.uc?.uc_name || "N/A",
    //   sortable: true,
    // },
    {
      name: <span className="text-[0.813rem]">Tehsil Name</span>,
      selector: (row) =>
        row.Tehsil?.tehsil_name || row.tehsil?.tehsil_name || "N/A",
      sortable: true,
    },
    {
      name: <span className="text-[0.813rem]">District Name</span>,
      selector: (row) =>
        row.District?.district_name || row.district?.district_name || "N/A",
      sortable: true,
    },
    {
      name: <span className="text-[0.813rem]">Status</span>,
      cell: (row) => {
        const status = row.status ?? row.Status;
        if (status === null || status === undefined) {
          return <span className="text-gray-400">-</span>;
        }
        return (
          <span
            className={`py-1 px-2 text-[0.75em] rounded ${
              status === 1 ? "bg-success text-white" : "bg-red-500 text-white"
            }`}
          >
            {status === 1 ? "Active" : "In-Active"}
          </span>
        );
      },
    },
  ];

  {
    if (
      permissions.includes("deh-update") ||
      permissions.includes("deh-delete")
    ) {
      columns.push({
        name: <span className="text-[0.813rem]">Action</span>,
        cell: (row) => (
          <div className="flex gap-2">
            {/* <Link to={`/dehs/edit/${row.deh_id || row.Deh_Id}`}>
          <i className="fa-solid fa-pen-to-square fa-lg"></i>
        </Link>
        <button
          onClick={() => showDeleteModal(row)}
          disabled={isDeleting}
          className={`${isDeleting ? "opacity-50 cursor-not-allowed" : ""}`}
        >
          <i className="fa fa-trash fa-lg text-primaryDark"></i>
        </button> */}

            <PermissionButton
              hasPermission={permissions.includes("deh-update")}
              to={`/dehs/edit/${row.deh_id || row.Deh_Id}`}
              faIcon="fa-solid fa-pen-to-square fa-lg"
              onlyIcon={true}
              enabledClass=""
              disabledClass="opacity-50 cursor-not-allowed"
            />
            <PermissionButton
              hasPermission={permissions.includes("deh-delete")}
              faIcon="fa fa-trash fa-lg text-primaryDark"
              onlyIcon
              onClick={() => showDeleteModal(row)}
              className="p-1 rounded-full bg-gray-200 text-[#137e81] hover:text-white"
              enabledClass=""
              disabledClass="opacity-50 cursor-not-allowed bg-gray-300"
            />
          </div>
        ),
      });
    }
  }

  return (
    <div>
      {loader && <Loader />}
      <div className="my-4 w-full flex flex-col space-y-3">
        <div className="flex lg:flex-row flex-col lg:justify-between gap-3 w-full">
          <h1 className="lg:text-[1.475rem] text-[1rem] text-[#212529] font-[Bold]">
            Deh
          </h1>
          <div className="flex gap-2">
            <button
              onClick={exportToExcel}
              disabled={isExporting}
              className={`flex items-center gap-2 px-4 py-1 text-sm bg-[#137e81] hover:bg-green-700 text-white rounded-md transition-colors ${
                isExporting ? "opacity-50 cursor-not-allowed" : ""
              }`}
            >
              <Download size={16} />
              {isExporting ? "Exporting..." : "Export Excel"}
            </button>
            <PermissionButton
              hasPermission={permissions.includes("deh-create")}
              to="add"
              faIcon="fa fa-plus"
              text="Add"
              className="flex items-center gap-2 py-[0.375rem] px-[0.75rem] rounded font-[InterLight] lg:text-[15px] text-[12px]"
              enabledClass="bg-[#be9609] text-white w-auto opacity-95 hover:opacity-85"
              disabledClass="bg-[#be9609] text-white opacity-50 cursor-not-allowed"
            />
          </div>
        </div>
        <Accordion>
          <AccordionSummary
            expandIcon={
              isFiltersVisible ? (
                <Minus color="#ffff" size={14} />
              ) : (
                <Plus size={14} color="#ffff" />
              )
            }
            sx={{
              backgroundColor: "#137e81",
              color: "#fff !important",
              "& .MuiAccordionSummary-content": { color: "#fff !important" },
            }}
            onClick={toggleFilters}
          >
            Filters
          </AccordionSummary>
          <AccordionDetails>
            <div className="flex gap-5 w-full">
              <ThinDropdownWithSearch required={false}
                label="District"
                id="district-dropdown"
                options={districts.map((d) => ({
                  value: d.district_id || d.District_Id,
                  label: d.district_name || d.District_Name,
                }))}
                value={tempDistrict}
                onChange={handleDistrictChange}
              />
              <ThinDropdownWithSearch required={false}
                label="Tehsil"
                id="tehsil-dropdown"
                options={tehsils.map((t) => ({
                  value: t.tehsil_id || t.Tehsil_Id,
                  label: t.tehsil_name || t.Tehsil_Name,
                }))}
                value={tempTehsil}
                onChange={handleTehsilChange}
              />
              {/* <ThinDropdownWithSearch required={false}
                label="UC"
                id="uc-dropdown"
                options={ucs.map((u) => ({
                  value: u.uc_id || u.UC_Id,
                  label: u.uc_name || u.UC_Name,
                }))}
                value={tempUc}
                onChange={handleUcChange}
              /> */}
              <div className="w-[30%] flex items-end gap-2">
                <button
                  onClick={clearFilters}
                  className="px-4 py-2 bg-[#be9609] text-white rounded hover:bg-gray-600 transition-colors"
                >
                  Clear
                </button>
                <button
                  onClick={applyFilters}
                  className="px-4 py-2 bg-[#137e81] text-white rounded hover:bg-[#0f6b6e] transition-colors"
                >
                  Apply
                </button>
              </div>
            </div>
          </AccordionDetails>
        </Accordion>
        {/* <br /> */}
        <div className="bg-white rounded-lg shadow w-full lg:mb-[30px] mt-4 mb-[7rem]">
          <div className="p-4 flex md:flex-row rounded-t-lg gradiant2 justify-between border-lightGray border-b ">
            <h1 className="lg:text-[0.875rem] text-[13px] tracking-[0.0625rem] text-[#856800] font-[bold] uppercase">
              Deh List
            </h1>
          </div>
          <div className="p-[1.25rem] w-full">
            <div className="flex lg:flex-row flex-col justify-between gap-2 mb-4 w-full">
              <PaginationDropdown
                value={pagination.pageSize}
                onChange={handlePageSizeChange}
                options={[
                  { value: 10, label: "10" },
                  { value: 20, label: "20" },
                  { value: 30, label: "30" },
                ]}
              />
              <input
                onChange={handleSearchChange}
                placeholder="Search..."
                type="search"
                className="bg-[#ebeef2] border-[#ebeef2] rounded-md px-3 py-1 outline-none"
              />
            </div>

            <DataTable
              columns={columns}
              data={dehs}
              customStyles={customStyles}
              highlightOnHover
              persistTableHead
            />

            <GeneralFooter
              pagination={pagination}
              handlePageChange={handlePageChange}
              handlePageSizeChange={handlePageSizeChange}
              dataLength={dehs.length}
            />
          </div>
        </div>

        <CustomConfirmModal
          isVisible={isModalVisible}
          onConfirm={handleDeleteConfirm}
          onCancel={handleDeleteCancel}
          message={`Are you sure you want to delete the deh "${selectedDehForDelete?.name}"?`}
          isLoading={isDeleting}
        />
      </div>
    </div>
  );
};

export default Deh;
